// Cliente push de Syphrax. DOS rutas:
//
//  1. Capacitor nativo (APK Android): arranca el SyphraxConnectionService — un
//     foreground service propio con WebSocket persistente al server. Notifica
//     localmente al usuario (sin pasar por Google ni FCM). Soberano.
//
//  2. Web (navegador / PWA): Web Push estándar (VAPID) contra `/push/subscribe`.

import { Capacitor, registerPlugin } from '@capacitor/core';

const API_BASE = 'https://api.syphrax.com';
const WS_URL = 'wss://api.syphrax.com/ws';

interface SyphraxConnectionPlugin {
  start(opts: { token: string; receiverTag: string; wsUrl?: string }): Promise<{ ok: boolean }>;
  stop(): Promise<{ ok: boolean }>;
  clearMessages(): Promise<{ ok: boolean }>;
  setForegroundState(opts: { visible: boolean }): Promise<{ ok: boolean }>;
}
const SyphraxConnection = registerPlugin<SyphraxConnectionPlugin>('SyphraxConnection');

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

export async function setupPushSubscription(
  token: string,
  receiverTagB64?: string,
): Promise<{ ok: boolean; reason?: string }> {
  if (isNativeAndroid()) {
    if (!receiverTagB64) return { ok: false, reason: 'no_receiver_tag' };
    try {
      await SyphraxConnection.start({ token, receiverTag: receiverTagB64, wsUrl: WS_URL });
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'native_start_failed:' + (e as Error).message };
    }
  }

  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'no_sw' };
  if (!('Notification' in window)) {
    // TypeScript narrowea 'window' a 'never' tras el check 'in window'. Capturamos
    // la referencia antes del narrowing y la casteamos para uso posterior.
    const w = globalThis as unknown as Window;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(w as unknown as { MSStream?: unknown }).MSStream;
    if (ios) {
      const m = navigator.userAgent.match(/OS (\d+)_(\d+)/);
      const major = m ? parseInt(m[1], 10) : 0;
      const minor = m ? parseInt(m[2], 10) : 0;
      // Web Push en iOS requiere 16.4+ y la PWA instalada (display-mode standalone).
      const tooOld = major < 16 || (major === 16 && minor < 4);
      if (tooOld) return { ok: false, reason: 'ios_too_old' };
      const standalone =
        w.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (!standalone) return { ok: false, reason: 'ios_not_standalone' };
    }
    return { ok: false, reason: 'no_notification_api' };
  }
  if (!('PushManager' in window)) return { ok: false, reason: 'no_push' };

  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await navigator.serviceWorker.ready;
  const vapidRes = await fetch(`${API_BASE}/push/vapid`);
  if (!vapidRes.ok) return { ok: false, reason: 'no_vapid' };
  const { public_key } = await vapidRes.json();
  if (!public_key) return { ok: false, reason: 'no_vapid_key' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
    });
  }

  const body = sub.toJSON();
  const res = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ endpoint: body.endpoint, keys: body.keys }),
  });
  if (!res.ok) return { ok: false, reason: `http_${res.status}` };
  return { ok: true };
}

export async function clearPushSubscription(token: string): Promise<void> {
  if (isNativeAndroid()) {
    try { await SyphraxConnection.stop(); } catch (_e) { /* ignore */ }
    return;
  }

  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await fetch(`${API_BASE}/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // ignore
  }
  await sub.unsubscribe();
}

export async function clearNativeMessageNotifications(): Promise<void> {
  if (!isNativeAndroid()) return;
  try { await SyphraxConnection.clearMessages(); } catch (_e) { /* ignore */ }
}

/**
 * Comunica al servicio nativo el estado de visibilidad de la WebView. Mientras
 * esta visible, el servicio no muestra notifs (la app esta abierta, el usuario
 * ya ve los mensajes en la UI). Al pasar a background, vuelve a mostrarlas.
 */
export async function setNativeForegroundState(visible: boolean): Promise<void> {
  if (!isNativeAndroid()) return;
  try { await SyphraxConnection.setForegroundState({ visible }); } catch (_e) { /* ignore */ }
}
